import React, { useState, useRef } from 'react';
import propTypes from 'prop-types';
import { localizedCollectionUrl } from '@utils/url-helpers';
import CutPaper from '@components/ui/CutPaper';
import StatusBadge from '@components/common/StatusBadge';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { MOBILE_MAX_WIDTH, BADGE_TYPES } from '@utils/enums';
import { sanitizeCmsImage } from '@utils/shopify-helpers';
import { createCutPaperPath } from '@utils/ui-utils';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper';
import 'swiper/css';

import ChevronIcon from '@components/icons/ChevronIcon';
import styles from './TopsTileNavigation.module.scss';

const TopsTileNavigation = ({ collections, heading }) => {
	const visibleCollections = collections ? collections.filter(collection => !collection.hideTile) : [];

	const { width: viewportWidth } = useWindowDimensions();

	const [activeSlide, setActiveSlide] = useState(0);

	const navigationPrevRef = useRef(null);
	const navigationNextRef = useRef(null);

	const navigationRefs = {
		prevEl: navigationPrevRef.current,
		nextEl: navigationNextRef.current,
	};

	const tileWidth = 160;
	const shouldHideNav = viewportWidth < MOBILE_MAX_WIDTH || viewportWidth / tileWidth > visibleCollections.length + 1;
	const shouldHidePrevNav = shouldHideNav || activeSlide === 0;
	const shouldHideNextNav = shouldHideNav || activeSlide + 1 >= collections.length / 4;

	const cutPaperPath = createCutPaperPath();

	return (
		<div className={styles.tileContainer}>
			<h3 className={styles.sectionTitle}>{heading}</h3>
			<div className={styles.tileTrack}>
				<Swiper
					className={styles.tileSlider}
					freeMode={{ sticky: false, enable: true }}
					direction='horizontal'
					modules={[Navigation]}
					autoplay={false}
					spaceBetween={16}
					slidesPerView={1}
					navigation={viewportWidth < MOBILE_MAX_WIDTH ? false : navigationRefs}
					onInit={swiper => setActiveSlide(swiper.activeIndex)}
					onSlideChange={swiper => setActiveSlide(swiper.activeIndex)}
					onBeforeInit={swiper => {
						swiper.params.navigation.prevEl = navigationPrevRef.current;
						swiper.params.navigation.nextEl = navigationNextRef.current;
					}}
				>
					{visibleCollections.length &&
						visibleCollections.map(collection => (
							<SwiperSlide
								key={collection.handle}
								className={styles.tile}
								style={{ backgroundImage: `url(${sanitizeCmsImage(collection.tileImage)})` }}
							>
								<a
									key={collection.handle}
									href={`${localizedCollectionUrl(collection.handle)}`}
									className={styles.tileLink}
									aria-label={collection.handle}
								>
									{' '}
								</a>
								<div>
									{collection.isLimited && <StatusBadge type={BADGE_TYPES.LIMITED} />}
									{collection.collectionIcon ? (
										<div className={styles.collectionIcon}>
											<img
												src={`${collection.collectionIconSmall || collection.collectionIcon}`}
												alt='collection logo'
											/>
										</div>
									) : (
										<div className={styles.collectionName}>
											<CutPaper
												content={<h3>{collection.title}</h3>}
												cutPaperPath={cutPaperPath}
												paddingSize={collection.title.length > 10 ? '4px 4px 12px' : '4px'}
												style={{ ...(collection.title.length > 10 && { top: '6px' }) }}
											/>
										</div>
									)}
								</div>
							</SwiperSlide>
						))}

					<div className={`${styles.prevButton} ${shouldHidePrevNav ? styles.hide : ''}`} ref={navigationPrevRef}>
						<ChevronIcon direction='left' color='#080593' />
					</div>

					<div className={`${styles.nextButton} ${shouldHideNextNav ? styles.hide : ''}`} ref={navigationNextRef}>
						<ChevronIcon direction='right' color='#080593' />
					</div>
				</Swiper>
			</div>
		</div>
	);
};

TopsTileNavigation.propTypes = {
	collections: propTypes.array.isRequired,
	heading: propTypes.string.isRequired,
};

export default TopsTileNavigation;
